import type { Configs, ShortcutsItem } from 'react-tailwindcss-datepicker';
import type { WeekStart } from '@/i18n/weekStart';
import { getDateRangeForPeriod, type DateRangeOptions } from '@/utils/time';
import type { ShortcutLabels } from '@/features/analysis/seach/util/Search';

function period(
    code: Parameters<typeof getDateRangeForPeriod>[0],
    dateRangeOptions: DateRangeOptions,
): { start: Date; end: Date } {
    const { start, end } = getDateRangeForPeriod(code, dateRangeOptions);
    return { start, end };
}

/** 無料ユーザー向けにロック表示＋クリックで案内するショートカットの表示文言（翻訳後ラベル） */
export function getPaidOnlyAnalysisShortcutDisplayTexts(labels: ShortcutLabels): string[] {
    return [labels.past3Months, labels.past6Months, labels.past12Months].filter(
        (x): x is string => typeof x === 'string' && x.length > 0,
    );
}

/**
 * 分析ページのカスタム期間用（react-tailwindcss-datepicker の configs）
 * 並び: 「過去〜」系（日→長期）を先に、その後に先週・今週・先月・今月。
 * 長期（過去3/6/12ヶ月相当）は有料機能だが、無料でも一覧に出し UI 側でロックする。
 */
export function buildAnalysisDatepickerConfigs(weekStartsOn: WeekStart, labels: ShortcutLabels): Configs {
    const dateRangeOptions: DateRangeOptions = { weekStartsOn };

    const shortcuts: Record<string, ShortcutsItem> = {};

    shortcuts.analysisPast7 = { text: labels.past7Days, period: period('PAST_7_DAYS', dateRangeOptions) };
    shortcuts.analysisPast14 = {
        text: labels.past14Days ?? labels.past7Days,
        period: period('PAST_14_DAYS', dateRangeOptions),
    };
    shortcuts.analysisPast30 = { text: labels.past30Days, period: period('PAST_30_DAYS', dateRangeOptions) };

    if (labels.past3Months && labels.past6Months && labels.past12Months) {
        shortcuts.analysisPast3 = {
            text: labels.past3Months,
            period: period('PAST_3_MONTHS', dateRangeOptions),
        };
        shortcuts.analysisPast6 = {
            text: labels.past6Months,
            period: period('PAST_6_MONTHS', dateRangeOptions),
        };
        shortcuts.analysisPast12 = {
            text: labels.past12Months,
            period: period('PAST_12_MONTHS', dateRangeOptions),
        };
    }

    shortcuts.analysisLastWeek = { text: labels.pastWeek, period: period('LAST_WEEK', dateRangeOptions) };
    shortcuts.analysisThisWeek = { text: labels.currentWeek, period: period('THIS_WEEK', dateRangeOptions) };
    shortcuts.analysisLastMonth = { text: labels.pastMonth, period: period('LAST_MONTH', dateRangeOptions) };
    shortcuts.analysisThisMonth = { text: labels.currentMonth, period: period('THIS_MONTH', dateRangeOptions) };

    return {
        shortcuts: shortcuts as NonNullable<Configs['shortcuts']>,
    };
}
